import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, BedDouble, CalendarDays, ShieldCheck, BarChart3, Settings, ScrollText, LogOut, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Oggi", icon: Home, id: "dashboard" },
  { to: "/prenotazioni", label: "Prenotazioni", icon: BedDouble, id: "bookings" },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, id: "calendar" },
  { to: "/schedine", label: "Schedine", icon: ShieldCheck, id: "schedine" },
  { to: "/movimentazione", label: "ISTAT / DMS", icon: BarChart3, id: "istat" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "settings" },
  { to: "/audit", label: "Registro attività", icon: ScrollText, id: "audit", admin: true },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive ? "bg-primary text-white" : "text-stone-700 hover:bg-stone-100"
  }`;

function SideNav({ items, onNavigate }) {
  return (
    <nav className="space-y-1">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} onClick={onNavigate} data-testid={`nav-${n.id}`}>
          <n.icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const items = NAV.filter((n) => !n.admin || user?.role === "admin");
  const doLogout = async () => {
    await logout();
    nav("/login");
  };
  const UserBox = (
    <div className="border-t pt-4 mt-4">
      <p className="text-sm font-semibold truncate" data-testid="current-user-email">{user?.email}</p>
      <p className="text-xs text-stone-500 uppercase tracking-wide">{user?.role === "admin" ? "Titolare" : "Operatore"}</p>
      <button onClick={doLogout} className="mt-3 flex items-center gap-2 text-sm text-stone-600 hover:text-primary" data-testid="logout-button">
        <LogOut className="h-4 w-4" /> Esci
      </button>
    </div>
  );
  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:flex w-64 flex-col border-r bg-white px-4 py-6 fixed inset-y-0 no-print">
        <div className="mb-8 px-2 flex items-center gap-3" data-testid="brand-sidebar">
          <img src="/logo-dimori.svg" alt="Dimori Guest House" className="h-14 w-14 rounded-xl" />
          <div>
            <p className="font-display text-lg font-extrabold leading-tight">Dimori Guest House</p>
            <p className="text-xs text-stone-500">Gestione check-in</p>
          </div>
        </div>
        <div className="flex-1"><SideNav items={items} /></div>
        {UserBox}
      </aside>
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b bg-white/90 backdrop-blur px-4 h-14 no-print">
        <div className="flex items-center gap-2" data-testid="brand-mobile">
          <img src="/logo-dimori.svg" alt="Dimori Guest House" className="h-9 w-9 rounded-lg" />
          <p className="font-display text-base font-extrabold">Dimori Guest House</p>
        </div>
        <button onClick={() => setOpen(true)} className="p-2" data-testid="mobile-menu-button" aria-label="Menu"><Menu className="h-5 w-5" /></button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-72">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <div className="mt-6"><SideNav items={items} onNavigate={() => setOpen(false)} /></div>
          {UserBox}
        </SheetContent>
      </Sheet>
      <main className="flex-1 min-w-0 lg:ml-64 px-4 sm:px-6 lg:px-10 py-6 pb-24 lg:pb-10 max-w-[1400px]">{children}</main>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 grid grid-cols-5 border-t bg-white h-16 no-print">
        {items.filter((n) => ["dashboard", "bookings", "calendar", "schedine", "istat"].includes(n.id)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottom-nav-${n.id}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[11px] font-medium ${isActive ? "text-primary" : "text-stone-500"}`}>
            <n.icon className="h-5 w-5" /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
